import cx from "classnames";
import { CylinderCompatibilityDialog } from "components/GasExchange/CylinderCompatibilityDialog";
import { CheckIconSvg } from "components/icons/CheckIconSvg";
import { useTranslationContext } from "context/translation";
import { FC, useState } from "react";
import { Image } from "react-datocms/image";
import { ProductBySlugQuery } from "services/datocms/generated";
import st from "./cylinder-type.module.scss";

export type CylinderType = NonNullable<
  ProductBySlugQuery["product"]
>["cylinderTypes"][number];

interface CylinderProps {
  cylinder: CylinderType;
  setSelectedCylinderType: (type: CylinderType | null) => void;
  selectedCylinderType: CylinderType | null;
  formattedPrice?: string;
  loading: boolean;
}

export const Cylinder: FC<CylinderProps> = ({
  setSelectedCylinderType,
  selectedCylinderType,
  cylinder,
  loading,
  formattedPrice,
}) => {
  const { image, title, description, compatibleMachines } = cylinder;
  const [showCompatilityModal, setShowCompatilityModal] = useState(false);
  const { cylinderCompatibleWithTheFollowing, viewCompatibility } =
    useTranslationContext();

  const selected = title === selectedCylinderType?.title;

  const handleSelectCylinderType = () => {
    if (!selected) {
      setSelectedCylinderType(cylinder);
    }
  };

  return (
    <>
      <div
        className="flex flex-col items-center w-full"
        bi-type="Co2 Cylinder - Select a cylinder"
        bi-id={title}
      >
        <div
          className={cx(
            "flex flex-col flex-1 h-full p-4 gap-1 items-center w-full bg-white rounded-2xl cursor-pointer relative min-h-[251px]",
            {
              "outline outline-secondary": selected,
            },
          )}
          onClick={() => {
            handleSelectCylinderType();
          }}
        >
          {selected && (
            <div className="absolute right-2 top-2 rounded-full w-[30px] h-[30px] bg-sodaStreamBlue flex justify-center items-center">
              <CheckIconSvg />
            </div>
          )}
          {image?.responsiveImage && (
            <Image
              data={image?.responsiveImage}
              className="h-[127px]"
              objectFit="contain"
            />
          )}
          <h3 className="text-gray-900 text-base font-semibold text-center">
            {title}
          </h3>
          <p
            className={cx(
              st.description,
              "text-[#002238] text-center font-[Poppins] text-sm font-light leading-[18px] w-3/5",
            )}
          >
            {description}
          </p>
          {loading ? (
            <div className="rounded-md w-20 h-[32px] bg-gray-200 animate-pulse-opacity" />
          ) : (
            <h5 className="text-base font-semibold leading-tight">
              {formattedPrice}
            </h5>
          )}
        </div>
        <span
          className="text-sodaStreamBlue text-center font-[Poppins] text-sm font-medium underline mt-3 cursor-pointer"
          onClick={() => setShowCompatilityModal(true)}
          bi-type="Co2 Cylinder - Select a cylinder"
          bi-id="View compatibility"
        >
          {viewCompatibility}
        </span>
      </div>
      {showCompatilityModal && (
        <CylinderCompatibilityDialog
          header={cylinderCompatibleWithTheFollowing}
          machines={compatibleMachines}
          onClose={() => setShowCompatilityModal(false)}
          onOkClick={() => setShowCompatilityModal(false)}
        />
      )}
    </>
  );
};
